/**
 * Form Submissions — raw intake behind the Lead Command Center's leads.
 *
 * Shows what actually arrived from forms/ads/chat in a human-readable
 * key/value layout (raw JSON stays behind an Advanced toggle) and links each
 * submission to the working lead created from it.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, FileInput } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { type LccFormSubmission, lccApi } from '@/lib/lcc-api';
import {
  formatLccDateTime,
  formatLccLabel,
  formatLccRelative,
  LccEmptyState,
  LccErrorCard,
  LccLoadingCard,
  LccPageHeader,
} from './lcc-shared';

export function FormSubmissionsView() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['lcc', 'form-submissions'],
    queryFn: () => lccApi.formSubmissions(),
  });

  return (
    <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-4">
      <LccPageHeader
        title="Form Submissions"
        blurb="Raw intake records — exactly what each person submitted through a form, ad, or chat before it became a working lead."
      />

      {isLoading && <LccLoadingCard />}
      {error != null && <LccErrorCard title="Couldn't load form submissions" error={error} />}
      {!isLoading && error == null && (
        <div className="flex flex-col gap-2.5">
          {(data?.items ?? []).length === 0 && (
            <Card>
              <LccEmptyState icon={FileInput} message="No form submissions yet." />
            </Card>
          )}
          {(data?.items ?? []).map((submission) => (
            <SubmissionCard key={submission.id} submission={submission} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionCard({ submission }: { submission: LccFormSubmission }) {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <Card className="space-y-2.5 p-3.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-[14px] font-semibold leading-tight">{submission.formName}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
            <span>{formatLccLabel(submission.source)}</span>
            <span title={submission.createdAt}>{formatLccRelative(submission.createdAt)}</span>
            {submission.submittedName && <span>{submission.submittedName}</span>}
            {submission.submittedEmail && <span className="break-all">{submission.submittedEmail}</span>}
            {submission.submittedPhone && <span className="tabular-nums">{submission.submittedPhone}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant={submission.status === 'discarded' ? 'destructive' : 'muted'}>
            {formatLccLabel(submission.status)}
          </Badge>
          {submission.leadId && (
            <Button asChild size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11.5px]">
              <Link to={`/demo/lcc/leads/${submission.leadId}`}>
                Open Lead
                <ArrowRight className="h-3 w-3" aria-hidden />
              </Link>
            </Button>
          )}
        </div>
      </div>

      <dl className="grid gap-x-4 gap-y-1.5 text-[12.5px] sm:grid-cols-2">
        {Object.entries(submission.payload).map(([key, value]) => (
          <div key={key} className="min-w-0">
            <dt className="text-[10px] font-semibold uppercase tracking-[0.7px] text-muted-foreground/70">
              {formatLccLabel(key)}
            </dt>
            <dd className="break-words">{String(value)}</dd>
          </div>
        ))}
      </dl>

      <button
        type="button"
        className="text-[11.5px] text-muted-foreground underline-offset-2 hover:underline"
        onClick={() => setShowRaw((v) => !v)}
      >
        {showRaw ? 'Hide advanced (raw JSON)' : 'Advanced: show raw JSON'}
      </button>
      {showRaw && (
        <pre className="overflow-x-auto rounded-md border border-border bg-muted/40 p-3 text-[11px] leading-relaxed">
          {JSON.stringify(submission.payload, null, 2)}
        </pre>
      )}
      {submission.leadTitle && (
        <div className="text-[11.5px] text-muted-foreground">
          Became lead: <span className="font-medium text-foreground">{submission.leadTitle}</span> ·{' '}
          {formatLccDateTime(submission.createdAt)}
        </div>
      )}
    </Card>
  );
}
